/**
 * Confirmation dialog for copying expenses to all dates
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import type { ExpenseItem } from '../../../types/ExpenseItem';
import { formatCurrency } from '../../../utils/formatters';

interface CopyExpensesDialogProps {
  open: boolean;
  expenseItems: ExpenseItem[];
  targetDateCount: number;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  errorMessage?: string | null;
  refreshOnly?: boolean;
}

export const CopyExpensesDialog: React.FC<CopyExpensesDialogProps> = ({
  open,
  expenseItems,
  targetDateCount,
  onClose,
  onConfirm,
  errorMessage,
  refreshOnly = false,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  useEffect(() => { setLocalError(null); }, [open]);
  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    setLocalError(null);
    try {
      await onConfirm();
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : 'Failed to copy expenses.');
    } finally {
      setSubmitting(false);
    }
  };
  // Release background aria-hidden before restoring trigger focus during the exit animation.
  return (
    <Dialog open={open} closeAfterTransition={false} onClose={submitting ? undefined : onClose}
      disableEscapeKeyDown={submitting} maxWidth="sm" fullWidth>
      <DialogTitle>Copy Expenses to All Dates?</DialogTitle>
      <DialogContent>
        {(errorMessage || localError) && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage || localError}</Alert>}
        <Alert severity="info" sx={{ mb: 2 }}>
          These expenses will be <strong>appended</strong> to existing expenses
          on other dates (not replaced).
        </Alert>

        <DialogContentText sx={{ mb: 2 }}>
          {refreshOnly ? 'Refresh the date data without appending expenses again.' : <>Copy the following {expenseItems.length} expense
          {expenseItems.length > 1 ? 's' : ''} to {targetDateCount} other date
          {targetDateCount > 1 ? 's' : ''}:</>}
        </DialogContentText>

        <List dense>
          {expenseItems.map((item, index) => (
            <ListItem key={index}>
              <ListItemText
                primary={item.name}
                secondary={formatCurrency(item.amount)}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} autoFocus>Cancel</Button>
        <Button onClick={() => void handleConfirm()} color="primary" variant="contained"
          disabled={submitting || (!refreshOnly && (expenseItems.length === 0 || targetDateCount === 0))}>
          {submitting ? 'Working...' : refreshOnly ? 'Refresh Dates' : 'Copy to All Dates'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
